import Vue from 'vue';
import { GetterTree, MutationTree, ActionTree } from 'vuex';
import { RootState } from '@/store'
import { Ingredient, IngredientProject } from '@/models/Ingredient'
import IngredientService from '@/services/IngredientService'
import { IngredientState } from './ingredients';
import Project from '@/models/Project';
const ingredientService = new IngredientService()

export interface ProjectIngredientState {
  projectIngredients: Array<IngredientProject>;
}

const state: ProjectIngredientState = {
  projectIngredients: [],
}


const mutations: MutationTree<ProjectIngredientState> = {
  ADD_PROJECT_INGREDIENT(state: any, ingredient: IngredientProject) {
    const index = state.projectIngredients.findIndex((obj: IngredientProject) => obj.id === ingredient.id)
    if (index < 0) {
      state.projectIngredients.push(ingredient)
    }
  },
  UPDATE_PROJECT_INGREDIENT(state: any, ingredient: IngredientProject) {
    const index = state.projectIngredients.findIndex((obj: IngredientProject) => obj.id === ingredient.id)
    if (index > -1) {
      Vue.set(state.projectIngredients, index, ingredient)
    }
  },
  DELETE_PROJECT_INGREDIENT(state: any, ingredient: IngredientProject) {
    const index = state.projectIngredients.findIndex((obj: IngredientProject) => obj.id === ingredient.id)
    if (index > -1) {
      Vue.delete(state.projectIngredients, index)
    }
  },
  RESET_PROJECT_INGREDIENTS_STATE(state: any) {
    state.projectIngredients = []
  }
}

const actions: ActionTree<ProjectIngredientState, any> = {
  clearProjectIngredients({ commit }) {
    commit('RESET_PROJECT_INGREDIENTS_STATE')
    ingredientService.clearLastDocProjectIngredients();
  },
  async fetchProjectIngredients({ commit }, payload: any) {
    const { projectId, options } = payload
    let sortBy, sortDesc = null;

    if (options && options.sortBy && options.sortDesc && options.sortBy.length === 1 && options.sortDesc.length === 1) {
      sortBy = options.sortBy[0]
      sortDesc = options.sortDesc[0]
    }

    await ingredientService.fetchProjectIngredients(projectId, sortBy, sortDesc).then((items: any) => {
      if (items) {
        items.forEach((item: IngredientProject) => {
          commit('ADD_PROJECT_INGREDIENT', item)
        })
      }
    })
  },
  async fetchProjectIngredient({ commit, state }, params): Promise<any> {
    const index = state.projectIngredients.findIndex((obj: IngredientProject) => obj.id === params.itemId)
    if (index < 0) {
      return await ingredientService.fetchProjectIngredient(params.projectId, params.itemId)
        .then((item: IngredientProject) => {
          commit('ADD_PROJECT_INGREDIENT', item)
          return Promise.resolve(item)
        })
        .catch((error) => {
          return Promise.reject(error)
        })
    } else {
      return Promise.resolve(state.projectIngredients[index])
    }
  },
  async addProjectIngredient({ commit }, item: IngredientProject): Promise<any> {
    await ingredientService.addProjectIngredient(item)
      .then((docRef: any) => {
        item.id = docRef.id
        commit('ADD_PROJECT_INGREDIENT', item)
        return Promise.resolve(item);
      })
      .catch((error) => {
        return Promise.reject(error);
      })
  },
  async editProjectIngredient({ commit, state }, item: IngredientProject) {
    await ingredientService.editProjectIngredient(item)
      .then(() => {
        const index = state.projectIngredients.findIndex((obj: IngredientProject) => obj.id === item.id)
        if (index < 0) {
          commit('ADD_PROJECT_INGREDIENT', item)
        } else {
          commit('UPDATE_PROJECT_INGREDIENT', item)
        }
      })
  },
  async deleteProjectIngredient({ commit, state }, item: IngredientProject) {
    await ingredientService.deleteProjectIngredient(item)
      .then(() => {
        commit('DELETE_PROJECT_INGREDIENT', item)
      })
  },
  async deleteMultipleEvaluations({ dispatch }, data: Array<IngredientProject>) {
    try {
      for await (const item of data) {
        dispatch('deleteProjectIngredient', item)
      }
    }
    catch (error) {
      console.error('Error in deleteMultipleEvaluations: ' + error)
      return false
    }
  },
  async importProjectIngredients({ dispatch }, { projectId, data }): Promise<any> {
    try {
      return await ingredientService.importEvaluations(projectId, data)
        .then((res: any) => {
          return Promise.resolve(res);
        })
        .catch((error: any) => {
          return Promise.reject(error);
        })
    }
    catch (error) {
      console.error('Error in importProjectIngredients: ' + error)
      return Promise.reject(error);
    }
  },
  async hasEvaluation({ state }, { projectId, item }): Promise<any> {

    // first check state
    const index = state.projectIngredients.findIndex((obj: IngredientProject) => {
      obj.reference === item.reference
    });

    // found match in state
    if (index > -1) return Promise.resolve(state.projectIngredients[index]);

    // currently need reference to check evaluation
    //if (typeof item.reference === 'undefined') return Promise.resolve(null)

    // validate projectId before DB call
    if (!projectId) return Promise.reject('No project id');

    // then check DB
    return await ingredientService.hasProjectIngredient(projectId, item)
      .then((item: any) => {
        return Promise.resolve(item)
      })
      .catch((error: any) => {
        return Promise.reject(error)
      })
  },

  async fetchEvaluationsByIngredient({ commit }, { projectId, item }): Promise<IngredientProject[]> {
    if (!projectId) return Promise.reject('No project id');

    return await ingredientService.fetchEvaluationsByIngredient(projectId, item)
      .then((items: any) => {
        items.forEach((item: IngredientProject) => {
          commit('ADD_PROJECT_INGREDIENT', item)
        })
        return Promise.resolve(items)
      })
      .catch((error: any) => {
        return Promise.reject(error)
      })
  },

  async fetchEvaluationsWithoutReference({ commit }, payload): Promise<IngredientProject[]> {
    const { projectId, limit, fromStart } = payload;
    return ingredientService.fetchEvaluationsWithoutReference(projectId, limit, fromStart)
      .then((items: IngredientProject[]) => {
        return Promise.resolve(items)
      })
      .catch((error: any) => {
        return Promise.reject(error)
      })
  },

  async batchUpdateEvaluationReference({ commit }, data: []): Promise<any> {
    return await ingredientService.batchUpdateEvaluationReference(data)
      .then((res) => {
        return Promise.resolve(res)
      })
      .catch((error: any) => {
        return Promise.reject(error)
      })
  }
}


const getters: GetterTree<ProjectIngredientState, RootState> = {
  getProjectIngredients: (state: any) => {
    return state.projectIngredients
  },
  getProjectIngredient: (state: any) => (id: string) => {
    return state.projectIngredients.find((item: any) => item.id === id);
  },
  getEvaluationsByIngredient: (state: any) => (ingredient: Ingredient) => {
    return state.projectIngredients.filter((item: IngredientProject) => item.reference === ingredient.reference )
  },
  getCustomProjectIngredients: (state: any) => {
    return state.projectIngredients.filter((item: IngredientProject) => item.custom)
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
